// =============================================================================
// FILE: packages/renderer/src/components/partners/FeaturedPartnersSection.tsx
// PURPOSE: Homepage "Build the Future of Infrastructure Together" band — a
//   premium Featured Partners showcase plus strategic investor allocation workflow
//   (invite workflow, My Partner Network dashboard, Become-a-Partner application).
//   Fully responsive; talks to /api/partners/*.
// =============================================================================

import { useEffect, useRef, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import {
  BadgeCheck, ExternalLink, X, Handshake, Users, Gift, Compass, Copy, Check, Loader2, Building2,
} from 'lucide-react';

const CATEGORIES = ['Engineering Firm', 'Contractor', 'Manufacturer', 'Technology Partner', 'BIM Partner', 'Education Partner'];

interface Partner { id: string; name: string; category: string; description: string; website: string; logo_url: string; verified: boolean }

function monogram(name: string): string {
  return name.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
}

export function FeaturedPartnersSection(): JSX.Element {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [invite, setInvite] = useState(false);
  const [network, setNetwork] = useState(false);
  const [become, setBecome] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void fetch('/api/partners/featured').then((r) => r.json()).then((d: { partners: Partner[] }) => setPartners(d.partners ?? [])).catch(() => { /* */ });
  }, []);

  return (
    <section data-testid="featured-partners-section" className="relative w-full border-t border-zinc-800/80 bg-gradient-to-b from-[#0b0d12] to-[#0a0c10] py-20 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.28em] text-teal-400/70 mb-3">RCD² Ecosystem · Partner Program</div>
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50">Build the Future of Infrastructure Together</h2>
          <p className="text-zinc-400 mt-4 text-[14px] leading-relaxed">
            Become a Featured Partner and help expand the RCD² ecosystem. Every successful invitation strengthens
            the network of designers, engineers, contractors, owners, and technology innovators.
          </p>
        </div>

        {/* CTAs */}
        <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
          <button type="button" data-testid="cta-become-partner" onClick={() => setBecome(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold transition"
            style={{ background: 'rgba(59,130,246,0.14)', border: '1px solid rgba(59,130,246,0.5)', color: '#93c5fd' }}>
            <Handshake className="h-4 w-4" /> Become a Featured Partner
          </button>
          <button type="button" data-testid="cta-invite-partner" onClick={() => setInvite(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold transition"
            style={{ background: 'rgba(52,211,153,0.14)', border: '1px solid rgba(52,211,153,0.5)', color: '#6ee7b7' }}>
            <Gift className="h-4 w-4" /> Invite a Strategic Investor
          </button>
          <button type="button" data-testid="cta-explore-ecosystem" onClick={() => gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold transition"
            style={{ background: 'rgba(251,191,36,0.14)', border: '1px solid rgba(251,191,36,0.5)', color: '#fcd34d' }}>
            <Compass className="h-4 w-4" /> Explore RCD² Ecosystem
          </button>
          <button type="button" data-testid="cta-my-network" onClick={() => setNetwork(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold transition"
            style={{ background: 'transparent', border: '1px solid rgba(148,163,184,0.35)', color: '#cbd5e1' }}>
            <Users className="h-4 w-4" /> My Partner Network
          </button>
        </div>

        {/* Showcase */}
        <div ref={gridRef} data-testid="partners-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
          {partners.length === 0 ? (
            <div className="col-span-full" style={{ textAlign: 'center', padding: '60px 24px', background: 'rgba(17,22,30,0.6)', border: '1px dashed rgba(94,234,212,0.18)', borderRadius: 16 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>🤝</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#e2ecfa', marginBottom: 8 }}>Partnership opportunities are currently available.</div>
              <div style={{ fontSize: 13, color: 'rgba(148,163,184,0.6)', maxWidth: 420, margin: '0 auto 20px' }}>
                Our first official partners will be announced here. Every partner listed on R2 is verified and represents a real, active relationship — no placeholder entries.
              </div>
              <div style={{ fontSize: 11, color: 'rgba(94,234,212,0.6)', background: 'rgba(94,234,212,0.06)', border: '1px solid rgba(94,234,212,0.15)', borderRadius: 8, padding: '8px 16px', display: 'inline-block' }}>
                Interested in partnering? Use the "Become a Partner" button above.
              </div>
            </div>
          ) : partners.map((p) => (
            <div key={p.id} data-testid={`partner-card-${p.id}`}
              className="rounded-xl p-5 transition hover:-translate-y-0.5"
              style={{ background: 'rgba(17,22,30,0.8)', border: '1px solid rgba(148,163,184,0.16)' }}>
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-lg grid place-items-center font-bold text-[13px] shrink-0"
                  style={{ background: 'linear-gradient(135deg,#1e293b,#0f172a)', border: '1px solid rgba(94,234,212,0.3)', color: '#5eead4' }}>
                  {p.logo_url ? <img src={p.logo_url} alt="" className="h-full w-full object-contain rounded-lg" /> : (monogram(p.name) || <Building2 className="h-5 w-5" />)}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13.5px] font-semibold text-zinc-100 truncate">{p.name}</span>
                    {p.verified && <BadgeCheck data-testid="partner-verified" className="h-4 w-4 text-teal-400 shrink-0" />}
                  </div>
                  <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium"
                    style={{ background: 'rgba(94,234,212,0.1)', color: 'rgba(94,234,212,0.85)', border: '1px solid rgba(94,234,212,0.25)' }}>
                    {p.category}
                  </span>
                </div>
              </div>
              <p className="text-[12px] text-zinc-400 mt-3 leading-relaxed">{p.description}</p>
              {p.website && (
                <a href={p.website} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-3 text-[11.5px] text-teal-300/80 hover:text-teal-200 transition">
                  Visit website <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Vision */}
        <div className="max-w-3xl mx-auto text-center mt-14 pt-10 border-t border-zinc-800/60">
          <p className="text-[15px] text-zinc-300 italic leading-relaxed">
            “RCD² grows through collaboration. Engineers, builders, technology providers, and innovators worldwide
            can participate in creating the next generation of infrastructure intelligence.”
          </p>
        </div>
      </div>

      {invite && <PartnerInviteModal onClose={() => setInvite(false)} />}
      {network && <PartnerNetworkModal onClose={() => setNetwork(false)} />}
      {become && <BecomePartnerModal onClose={() => setBecome(false)} />}
    </section>
  );
}

// ── Shared modal shell ───────────────────────────────────────────────────────
function ModalShell({ title, subtitle, testid, onClose, children }:
  { title: string; subtitle: string; testid: string; onClose: () => void; children: React.ReactNode }): JSX.Element {
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 10050, display: 'grid', placeItems: 'center', padding: 20, background: 'rgba(4,7,13,0.7)', backdropFilter: 'blur(5px)' }} onClick={onClose}>
      <div data-testid={testid} onClick={(e) => e.stopPropagation()} style={{ width: 'min(520px,100%)', maxHeight: '90vh', overflowY: 'auto', borderRadius: 16, background: '#0b1220', border: '1px solid rgba(94,234,212,0.28)', boxShadow: '0 24px 70px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '16px 20px', borderBottom: '1px solid rgba(94,234,212,0.14)' }}>
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#e6fffb' }}>{title}</div>
            <div style={{ fontSize: 11.5, color: 'rgba(148,163,184,0.75)', marginTop: 2 }}>{subtitle}</div>
          </div>
          <button type="button" data-testid="partner-modal-close" onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'rgba(148,163,184,0.6)', cursor: 'pointer' }}><X style={{ width: 16, height: 16 }} /></button>
        </div>
        <div style={{ padding: '18px 20px 22px' }}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}

const inputStyle: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 9, background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(148,163,184,0.28)', color: '#e2e8f0', fontSize: 13, outline: 'none' };
const labelStyle: React.CSSProperties = { display: 'block', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(148,163,184,0.7)', marginBottom: 4, marginTop: 12 };
const primaryBtn: React.CSSProperties = { width: '100%', marginTop: 16, padding: '11px', borderRadius: 10, fontWeight: 700, fontSize: 13.5, cursor: 'pointer', color: '#04110f', background: 'linear-gradient(135deg,#34d399,#10b981)', border: 'none' };

function CategorySelect({ value, onChange }: { value: string; onChange: (v: string) => void }): JSX.Element {
  return (
    <select data-testid="partner-category-select" value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
      <option value="">Select industry category…</option>
      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
    </select>
  );
}

function PartnerInviteModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [f, setF] = useState({ inviter_name: '', inviter_email: '', partner_name: '', contact_name: '', email: '', category: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ invite_link: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const r = await fetch('/api/partners/invite', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f) });
      const d = await r.json();
      if (!r.ok) { setError(typeof d.detail === 'string' ? d.detail : 'Could not create invitation.'); }
      else setResult(d);
    } catch { setError('Network error.'); } finally { setBusy(false); }
  };

  return (
    <ModalShell testid="partner-invite-modal" onClose={onClose} title="Strategic Investor Invitation" subtitle="Investor Equity Allocation: up to 5% ownership allocation reserved for qualified strategic investors, subject to investment terms and agreements.">
      {result ? (
        <div data-testid="invite-success">
          <p style={{ fontSize: 13, color: '#a7f3d0', lineHeight: 1.6 }}>Invitation created. Share this link — it tracks back to you automatically:</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <input readOnly value={result.invite_link} data-testid="invite-link" style={{ ...inputStyle, marginTop: 0 }} />
            <button type="button" data-testid="copy-invite-link" onClick={() => { void navigator.clipboard?.writeText(result.invite_link); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
              style={{ padding: '0 12px', borderRadius: 9, background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.4)', color: '#6ee7b7', cursor: 'pointer' }}>
              {copied ? <Check style={{ width: 15, height: 15 }} /> : <Copy style={{ width: 15, height: 15 }} />}
            </button>
          </div>
          <button type="button" onClick={onClose} style={{ ...primaryBtn }}>Done</button>
        </div>
      ) : (
        <form onSubmit={submit}>
          <label style={labelStyle}>Partner / company name *</label>
          <input data-testid="invite-partner-name" required value={f.partner_name} onChange={set('partner_name')} style={inputStyle} placeholder="Acme Engineering" />
          <label style={labelStyle}>Contact name</label>
          <input data-testid="invite-contact-name" value={f.contact_name} onChange={set('contact_name')} style={inputStyle} placeholder="Jane Doe" />
          <label style={labelStyle}>Partner email *</label>
          <input data-testid="invite-email" required type="email" value={f.email} onChange={set('email')} style={inputStyle} placeholder="contact@acme.com" />
          <label style={labelStyle}>Industry category</label>
          <CategorySelect value={f.category} onChange={(v) => setF({ ...f, category: v })} />
          <label style={labelStyle}>Your email (for investor allocation records)</label>
          <input data-testid="invite-your-email" type="email" value={f.inviter_email} onChange={set('inviter_email')} style={inputStyle} placeholder="you@firm.com" />
          <label style={labelStyle}>Optional message</label>
          <textarea data-testid="invite-message" value={f.message} onChange={set('message')} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="I'd love for you to join the RCD² ecosystem…" />
          {error && <p data-testid="invite-error" style={{ color: '#fca5a5', fontSize: 12, marginTop: 10 }}>{error}</p>}
          <button type="submit" data-testid="invite-submit" disabled={busy} style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Generating…' : 'Generate Invitation Link'}
          </button>
        </form>
      )}
    </ModalShell>
  );
}

function PartnerNetworkModal({ onClose }: { onClose: () => void }): JSX.Element {
  const stored = (() => { try { return JSON.parse(localStorage.getItem('rcd2.auth') ?? '{}')?.email ?? ''; } catch { return ''; } })();
  const [email, setEmail] = useState<string>(stored);
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async (em: string) => {
    if (!em.trim()) return; setBusy(true);
    try { const r = await fetch(`/api/partners/network?email=${encodeURIComponent(em.trim())}`); setData(await r.json()); }
    catch { /* */ } finally { setBusy(false); }
  };
  useEffect(() => { if (stored) void load(stored); /* eslint-disable-next-line */ }, []);

  const stat = (k: string): string => String((data?.[k] as number | null) ?? '—');

  return (
    <ModalShell testid="partner-network-modal" onClose={onClose} title="My Partner Network" subtitle="Track invitations, active partners, and strategic investor equity allocation records.">
      <div style={{ display: 'flex', gap: 8 }}>
        <input data-testid="network-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" style={{ ...inputStyle, marginTop: 0 }} />
        <button type="button" data-testid="network-load" onClick={() => void load(email)} disabled={busy} style={{ padding: '0 14px', borderRadius: 9, background: 'rgba(94,234,212,0.14)', border: '1px solid rgba(94,234,212,0.4)', color: '#5eead4', cursor: 'pointer', fontWeight: 700, fontSize: 12.5 }}>
          {busy ? <Loader2 style={{ width: 15, height: 15 }} className="animate-spin" /> : 'View'}
        </button>
      </div>

      {data && (
        <>
          <div data-testid="network-stats" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 16 }}>
            {[
              { k: 'invited', label: 'Invited', tone: '#93c5fd' },
              { k: 'pending', label: 'Pending', tone: '#fcd34d' },
              { k: 'active', label: 'Active', tone: '#6ee7b7' },
            ].map((s) => (
              <div key={s.k} style={{ padding: '12px', borderRadius: 10, background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(148,163,184,0.16)', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.tone }}>{stat(s.k)}</div>
                <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(148,163,184,0.7)' }}>{s.label}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <div style={{ flex: 1, padding: '12px', borderRadius: 10, background: 'rgba(16,32,28,0.7)', border: '1px solid rgba(52,211,153,0.3)', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#34d399' }}>Up to 5%</div>
              <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(148,163,184,0.7)' }}>Equity allocation block</div>
            </div>
            <div style={{ flex: 1, padding: '12px', borderRadius: 10, background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(148,163,184,0.16)', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#fcd34d' }}>{data.ranking ? `#${stat('ranking')}` : '—'}</div>
              <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(148,163,184,0.7)' }}>Partner ranking</div>
            </div>
          </div>
          {typeof data.share_link === 'string' && (
            <>
              <label style={labelStyle}>Your shareable invitation link</label>
              <input data-testid="network-share-link" readOnly value={data.share_link} style={{ ...inputStyle, marginTop: 0 }} />
            </>
          )}
        </>
      )}
    </ModalShell>
  );
}

function BecomePartnerModal({ onClose }: { onClose: () => void }): JSX.Element {
  const [f, setF] = useState({ name: '', category: '', description: '', website: '', contact_email: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const r = await fetch('/api/partners/apply', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f) });
      const d = await r.json();
      if (!r.ok) setError(typeof d.detail === 'string' ? d.detail : 'Could not submit application.');
      else setDone(true);
    } catch { setError('Network error.'); } finally { setBusy(false); }
  };

  return (
    <ModalShell testid="become-partner-modal" onClose={onClose} title="Become a Featured Partner" subtitle="Applications are reviewed before appearing publicly.">
      {done ? (
        <div data-testid="apply-success">
          <p style={{ fontSize: 13.5, color: '#a7f3d0', lineHeight: 1.6 }}>Thank you — your application has been received. Featured Partners appear on the homepage after admin approval.</p>
          <button type="button" onClick={onClose} style={primaryBtn}>Done</button>
        </div>
      ) : (
        <form onSubmit={submit}>
          <label style={labelStyle}>Partner / company name *</label>
          <input data-testid="apply-name" required value={f.name} onChange={set('name')} style={inputStyle} />
          <label style={labelStyle}>Category</label>
          <CategorySelect value={f.category} onChange={(v) => setF({ ...f, category: v })} />
          <label style={labelStyle}>Short description</label>
          <textarea data-testid="apply-description" value={f.description} onChange={set('description')} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          <label style={labelStyle}>Website</label>
          <input data-testid="apply-website" value={f.website} onChange={set('website')} style={inputStyle} placeholder="https://" />
          <label style={labelStyle}>Contact email</label>
          <input data-testid="apply-email" type="email" value={f.contact_email} onChange={set('contact_email')} style={inputStyle} />
          {error && <p style={{ color: '#fca5a5', fontSize: 12, marginTop: 10 }}>{error}</p>}
          <button type="submit" data-testid="apply-submit" disabled={busy} style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Submitting…' : 'Submit Application'}
          </button>
        </form>
      )}
    </ModalShell>
  );
}
